// components/dashboard/ProjectTableRow.jsx

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/AuthContext";
import { TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { EditStagesDialog } from "./EditStagesDialog";
import { inr, getStageColor } from "@/App";
import { Trash2, Pencil, Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { confirmToast } from "@/utils/ConfirmToast";

const statusVariantMap = {
  ACTIVE: "default",
  COMPLETED: "secondary",
  INACTIVE: "outline",
  UNREALISED: "destructive",
};

export function ProjectTableRow({ project, onUpdate, onDelete, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const showFinance = user?.role !== "TEAM";

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedProject, setEditedProject] = useState(project);

  // If the project prop changes from the parent, reset the local state.
  // This is important if data is re-fetched, ensuring the row shows the latest data.
  useEffect(() => {
    setEditedProject(project);
  }, [project]);

  // Generic handler to update the staged changes
  const handleChange = (field, value) => {
    setEditedProject((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Find only the fields that have actually changed
    const changes = Object.keys(editedProject).reduce((acc, key) => {
      if (editedProject[key] !== project[key]) {
        acc[key] = editedProject[key];
      }
      return acc;
    }, {});

    if (Object.keys(changes).length === 0) {
      toast("No changes to save.");
      setIsEditing(false);
      setIsSaving(false);
      return;
    }

    const promise = onUpdate(project.project_id, changes);

    toast
      .promise(promise, {
        loading: "Saving changes...",
        success: () => {
          setIsEditing(false); // Exit edit mode on success
          return "Project updated successfully!";
        },
        error: (err) => `Failed to update: ${err.message || "Unknown error"}`,
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleCancel = () => {
    setEditedProject(project); // Revert any local changes
    setIsEditing(false);
  };

  return (
    <TableRow>
      {/* --- STATIC CELLS (Can be made editable if needed) --- */}
      <TableCell>{project.code}</TableCell>
      <TableCell className="whitespace-nowrap">
        <Link
          to={`/projects/${project.project_id}`}
          className="hover:underline font-semibold"
        >
          {project.name}
        </Link>
      </TableCell>

      {/* --- EDITABLE CELLS --- */}
      <TableCell>
        {isEditing && isAdmin ? (
          <Input
            value={editedProject.client || ""}
            onChange={(e) => handleChange("client", e.target.value)}
            className="h-8 w-fit"
          />
        ) : (
          project.client || "—"
        )}
      </TableCell>

      <TableCell>
        {isEditing && isAdmin ? (
          <Input
            value={editedProject.client_contact || ""}
            onChange={(e) => handleChange("client_contact", e.target.value)}
            className="h-8 w-[118px]"
          />
        ) : (
          project.client_contact || "—"
        )}
      </TableCell>
      <TableCell>
        {isEditing && isAdmin ? (
          <Input
            value={editedProject.client_company || ""}
            onChange={(e) => handleChange("client_company", e.target.value)}
            className="h-8 w-[140px]"

          />
        ) : (
          project.client_company || "—"
        )}
      </TableCell>

      <TableCell>
        {isEditing && isAdmin ? (
          <Input
            value={editedProject.gst_details || ""}
            onChange={(e) => handleChange("gst_details", e.target.value)}
            className="h-8 w-[120px]"

          />
        ) : (
          project.gst_details || "—"
        )}
      </TableCell>
      {/* Status column */}
      {(user?.role === "ADMIN" || user?.role === "PM") && (
        <TableCell>
          {isEditing && isAdmin ? (
            <Select
              onValueChange={(v) => handleChange("status", v)}
              value={editedProject.status}
            >
              <SelectTrigger className="w-[120px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="UNREALISED">Unrealised</SelectItem>
                <SelectItem value="UPCOMING">Upcoming</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            isAdmin ? <Badge variant={statusVariantMap[project.status]}>{project.status}</Badge> : null
          )}
        </TableCell>
      )}


      <TableCell>
        {isEditing && isAdmin ? (
          <Select
            onValueChange={(v) => handleChange("current_stage_idx", v)}
            value={editedProject.current_stage_idx}
          >
            <SelectTrigger className="w-[120px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {project?.stages?.map(({ name: stage }, i) => (
                <SelectItem key={i} value={stage}>
                  <span className={`px-2 py-1 rounded-sm ${getStageColor(stage)}`}>
                    {stage}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className={`px-2 py-1 rounded-md text-sm font-medium ${getStageColor(project.current_stage_idx)}`}>
            {project.current_stage_idx}
          </span>
        )}
      </TableCell>

      {/* --- FINANCE CELLS (Read-only for now) --- */}
      {showFinance && (
        <>
          <TableCell className="text-right">
            {isEditing && isAdmin ? (
              <Input
                type="number"
                value={
                  editedProject.fee_total === 0
                    ? "0"
                    : editedProject.fee_total || ""
                }
                onChange={(e) => handleChange("fee_total", parseFloat(e.target.value) || 0)}
                className="h-8 w-[110px] text-right"
              />
            ) : (
              inr.format(project.fee_total)
            )}
          </TableCell>
          <TableCell className="text-right">
            {inr.format(project.fees_received)}
          </TableCell>
          <TableCell className="text-right">
            {inr.format(project.fee_total - project.fees_received)}
          </TableCell>
          {/* <TableCell className="text-right">{project.total_hours}</TableCell> */}
          <TableCell className="text-right">{Math.round(project.total_hours)}</TableCell>
          <TableCell className="text-right">
            {inr.format(project.direct_cost)}
          </TableCell>
          <TableCell className="text-right">
            {inr.format(project.overhead || 0)}
          </TableCell>
          <TableCell className="text-right font-semibold">
            {inr.format(project.balance || 0)}
          </TableCell>
        </>
      )}

      {/* --- STICKY ACTION CELL --- */}
      {isAdmin && (
        <TableCell className="sticky right-0 bg-card border-l">
          <div className="flex items-center justify-center gap-1">
            {isEditing ? (
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-green-600 hover:text-green-600"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-red-600 hover:text-red-600"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <EditStagesDialog
                  projectId={project.project_id}
                  projectName={project.name}
                  totalCost={project.fee_total}
                  onSaved={onSaved}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() =>
                    confirmToast(
                      `${project.name}: This cannot be undone. Once deleted, this project is gone forever.`,
                      () => onDelete(project.project_id)
                    )
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}
